import {
	type UpdateVerificationFlowWithCodeMethod,
	type VerificationFlow,
	VerificationFlowState,
} from "@ory/kratos-client";
import type { AxiosError } from "axios";
import type React from "react";
import { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
	ContentContainer,
	ContentContainerWrapper,
} from "~/components/ContentContainer";
import { Flow, handleFlowError } from "~/components/KratosFlow";
import { DefaultLayout } from "~/components/Layout";
import { Loading } from "~/components/Loading";
import { useModal } from "~/components/Modal";
import { useSnackBar } from "~/components/SnackBar";
import { kratos, SessionContext } from "~/ory";
import { createFlowErrorHandler } from "~/utils/errorHandler";

export const Verify: React.FC = () => {
	const session = useContext(SessionContext);
	const [flow, setFlow] = useState<VerificationFlow | undefined>();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [openModal, closeModal] = useModal();
	const [openSnackBar] = useSnackBar();

	// Get ?flow=... from the URL
	const returnTo = searchParams.get("return_to");
	const flowId = searchParams.get("flow");

	// In this effect we either initiate a new verification flow, or we fetch an existing verification flow.
	// biome-ignore lint/correctness/useExhaustiveDependencies: openSnackBar/openModal/closeModal are intentionally excluded. They are used only for fire-and-forget error display, and this effect should re-fetch on session/flow/URL changes only. They are stable in production (SnackBar/Modal providers memoize open/close via useCallback), so excluding them changes nothing at runtime; biome just cannot verify that stability across the context boundary.
	useEffect(() => {
		if (!session) return;
		// If the router is not ready yet, or we already have a flow, do nothing.
		if (flow) {
			return;
		}

		// Fetching/initializing a flow can fail at the network level (request aborted,
		// offline) with no HTTP response. handleFlowError can't classify those and re-rejects,
		// so we add a terminal handler to avoid an unhandled promise rejection (mirrors submit()).
		const onFlowError = createFlowErrorHandler(
			openSnackBar,
			openModal,
			closeModal,
		);

		// If ?flow=.. was in the URL, we fetch it
		if (flowId) {
			kratos
				.getVerificationFlow({ id: String(flowId) })
				.then(({ data }) => {
					// We received the flow - let's use its data and render the form!
					setFlow(data);
				})
				.catch(handleFlowError(navigate, "verify", setFlow))
				.catch(onFlowError);
			return;
		}

		// Otherwise we initialize it
		kratos
			.createBrowserVerificationFlow({
				returnTo: returnTo ? String(returnTo) : undefined,
			})
			.then(({ data }) => {
				setFlow(data);
			})
			.catch(handleFlowError(navigate, "verify", setFlow))
			.catch(onFlowError);
	}, [session, flowId, returnTo, flow, navigate]);

	useEffect(() => {
		if (!flow) return;
		if (flow.state === "passed_challenge") {
			navigate("/complete-verification");
		}
	}, [flow, navigate]);

	if (!session) return <p>Unauthenticated</p>;
	if (!flow) return <Loading />;

	const onSubmit = (
		values: UpdateVerificationFlowWithCodeMethod & { method: "code" },
	) => {
		setSearchParams({ flow: String(flow?.id) });
		return kratos
			.updateVerificationFlow({
				flow: String(flow?.id),
				updateVerificationFlowBody: values,
			})
			.then(({ data }) => {
				// For now however we just want to redirect home!
				setFlow(data);
			})
			.catch(handleFlowError(navigate, "verify", setFlow))
			.catch((err: AxiosError<VerificationFlow>) => {
				// If the previous handler did not catch the error it's most likely a form validation error
				if (err.response?.status === 400) {
					// Yup, it is!
					setFlow(err.response?.data);
					return;
				}

				createFlowErrorHandler(openSnackBar, openModal, closeModal)(err);
			});
	};

	return (
		<>
			<title>Verification | TIER IV Account</title>
			<DefaultLayout>
				<ContentContainerWrapper>
					<ContentContainer>
						<h1 className="mt-10 text-center font-normal">
							EMAIL VERIFICATION
						</h1>
						<Flow
							onSubmit={onSubmit}
							flow={flow}
							context={{
								withIcon: true,
							}}
							setInputDefaultValues={[
								{
									query: {
										node_type: "input",
										name: "email",
									},
									value: session.account.identity.email,
								},
							]}
							nodeDisablers={[
								{
									target: { name: "method" },
									keys: [
										flow.active === "code" &&
										flow.state === VerificationFlowState.SentEmail
											? "code"
											: "email",
									],
									allowEmpty: false,
									allowNotChanged: true,
								},
							]}
						/>
						<p className="mt-6 text-center">
							<Link to="/">Go back Home</Link>
						</p>
					</ContentContainer>
				</ContentContainerWrapper>
			</DefaultLayout>
		</>
	);
};
